import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EarningsReportingSummary.module.scss';
import { fitVisibleTopicCount } from './TopicsCell.utils';
import type { EarningsTopic, EarningsTopicClickHandler } from './types';

interface TopicsCellProps {
  topics: EarningsTopic[];
  onTopicClick?: EarningsTopicClickHandler;
}

export const TopicsCell = ({ topics, onTopicClick }: TopicsCellProps) => {
  const { t } = useTranslation(['widget']);
  const containerRef = useRef<HTMLParagraphElement>(null);
  const topicMeasurementRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const separatorMeasurementRef = useRef<HTMLSpanElement>(null);
  const overflowMeasurementRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(topics.length);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  // A stable array prevents the layout effect from rerunning after its own state update.
  const overflowLabels = useMemo(
    () =>
      Array.from({ length: Math.max(topics.length - 1, 0) }, (_, index) =>
        t('widget:themeOverview.tabs.earnings.reportingSummary.moreTopics', {
          count: index + 1,
        })
      ),
    [topics.length, t]
  );

  useLayoutEffect(() => {
    if (topics.length <= 1) {
      setVisibleCount(topics.length);
      setIsOverflowOpen(false);
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const update = () => {
      const nextVisibleCount = fitVisibleTopicCount({
        containerWidth: container.getBoundingClientRect().width,
        topicWidths: Array.from(
          { length: topics.length },
          (_, index) =>
            topicMeasurementRefs.current[index]?.getBoundingClientRect()
              .width ?? 0
        ),
        separatorWidth:
          separatorMeasurementRef.current?.getBoundingClientRect().width ?? 0,
        overflowWidths: overflowLabels.map(
          (_, index) =>
            overflowMeasurementRefs.current[index]?.getBoundingClientRect()
              .width ?? 0
        ),
      });

      setVisibleCount(current =>
        current === nextVisibleCount ? current : nextVisibleCount
      );
      if (nextVisibleCount === topics.length) {
        setIsOverflowOpen(false);
      }
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);

    return () => observer.disconnect();
  }, [topics, overflowLabels]);

  if (topics.length === 0) {
    return <span className={styles.emptyValue}>-</span>;
  }

  const hiddenCount = topics.length - visibleCount;
  const handleTopicClick = (
    event: MouseEvent<HTMLElement>,
    topic: EarningsTopic
  ) => {
    event.stopPropagation();
    if (!topic.themeId) {
      return;
    }
    setIsOverflowOpen(false);
    onTopicClick?.(topic.themeId);
  };
  return (
    <p ref={containerRef} className={styles.topics}>
      {topics.map((topic, index) => (
        <span
          key={`${topic.label}-${index}`}
          data-topic-item=""
          hidden={index >= visibleCount}
          className={styles.topicItem}
        >
          {topic.themeId && onTopicClick ? (
            <span
              data-topic=""
              className={clsx(
                styles.topicButton,
                THEME_TAG_COLOR_CLASS_MAP[topic.themeType]
              )}
              onClick={event => handleTopicClick(event, topic)}
            >
              {topic.label}
            </span>
          ) : (
            <span
              data-topic=""
              className={THEME_TAG_COLOR_CLASS_MAP[topic.themeType]}
            >
              {topic.label}
            </span>
          )}
          {index < visibleCount - 1 && (
            <span data-separator="" className={styles.topicSeparator}>
              ·
            </span>
          )}
        </span>
      ))}
      <DropdownMenu
        open={isOverflowOpen && hiddenCount > 0}
        onOpenChange={setIsOverflowOpen}
        className={styles.topicsMenu}
        triggerNode={
          <span
            role="button"
            tabIndex={0}
            data-overflow=""
            hidden={hiddenCount <= 0}
            className={styles.overflowCount}
            onClick={event => event.stopPropagation()}
          >
            <span data-overflow-label="" className={styles.overflowCountLabel}>
              {hiddenCount > 0
                ? t(
                    'widget:themeOverview.tabs.earnings.reportingSummary.moreTopics',
                    { count: hiddenCount }
                  )
                : ''}
            </span>
          </span>
        }
      >
        <div
          className={styles.topicsMenuList}
          data-testid="earnings-topics-overflow-panel"
        >
          {isOverflowOpen &&
            hiddenCount > 0 &&
            topics.slice(visibleCount).map(topic =>
              topic.themeId && onTopicClick ? (
                <button
                  type="button"
                  key={`${topic.themeType}:${topic.label}`}
                  className={clsx(
                    styles.topicsMenuItem,
                    styles.topicsMenuButton,
                    THEME_TAG_COLOR_CLASS_MAP[topic.themeType]
                  )}
                  onClick={event => handleTopicClick(event, topic)}
                >
                  {topic.label}
                </button>
              ) : (
                <span
                  key={`${topic.themeType}:${topic.label}`}
                  className={clsx(
                    styles.topicsMenuItem,
                    THEME_TAG_COLOR_CLASS_MAP[topic.themeType]
                  )}
                >
                  {topic.label}
                </span>
              )
            )}
        </div>
      </DropdownMenu>
      <span className={styles.topicsMeasurement} aria-hidden="true">
        {topics.map((topic, index) => (
          <span
            key={`${topic.label}-${index}`}
            ref={node => {
              topicMeasurementRefs.current[index] = node;
            }}
            data-topic-measurement=""
            className={styles.topicMeasurementItem}
          >
            {topic.label}
          </span>
        ))}
        <span
          ref={separatorMeasurementRef}
          data-separator-measurement=""
          className={clsx(styles.topicMeasurementItem, styles.topicSeparator)}
        >
          ·
        </span>
        {overflowLabels.map((label, index) => (
          <span
            key={label}
            ref={node => {
              overflowMeasurementRefs.current[index] = node;
            }}
            data-overflow-measurement=""
            className={clsx(
              styles.topicMeasurementItem,
              styles.overflowMeasurement
            )}
          >
            {label}
          </span>
        ))}
      </span>
    </p>
  );
};
